// helpers/authPersist.js
// Persist state.auth vào localStorage để tránh gọi API /me mỗi lần reload
// Không dùng redux-persist — tự quản lý nhẹ, đúng chỗ cần

import {getTokenKey} from "./localStorage";

const AUTH_PERSIST_KEY = '_wAuthState';
const TOKEN_KEY = '_wTokenKey';

/**
 * Đọc auth state đã lưu từ localStorage.
 * Trả về object hoặc undefined nếu chưa có / lỗi parse.
 *
 * Lưu ý: Nếu token invalid (null/undefined/rác), CHỈ xóa persist state,
 * KHÔNG xóa _wTokenKey — việc quản lý token key là của auth flow.
 */
export const loadPersistedAuth = () => {
    try {
        const raw = localStorage.getItem(AUTH_PERSIST_KEY);
        if (!raw) return undefined;

        const data = JSON.parse(raw);

        // Guard: parse ra không phải object thuần
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            localStorage.removeItem(AUTH_PERSIST_KEY);
            return undefined;
        }

        const token = getTokenKey();
        const isInvalidToken = !token ||
            token === 'null' ||
            token === 'undefined' ||
            token === '';

        if (isInvalidToken) {
            // Chỉ xóa persist state — KHÔNG xóa TOKEN_KEY
            // Token key null có thể do network lỗi tạm thời khi refresh,
            // không nên xóa luôn để tránh mất session hợp lệ
            localStorage.removeItem(AUTH_PERSIST_KEY);
            return undefined;
        }

        // Kiểm tra exp nếu có (token thật sự hết hạn)
        if (data.exp && Date.now() / 1000 > data.exp) {
            localStorage.removeItem(AUTH_PERSIST_KEY);
            localStorage.removeItem(TOKEN_KEY);
            return undefined;
        }

        return data;
    } catch {
        return undefined;
    }
};

/**
 * Lưu auth state vào localStorage.
 * Chỉ lưu { user, login, token, success, exp } — bỏ qua field thừa.
 * Không lưu khi:
 *   - authState null/undefined
 *   - failure = true
 *   - token null/undefined/rác (tránh persist session hỏng do network lỗi)
 */
export const savePersistedAuth = (authState) => {
    try {
        if (!authState || authState.failure) {
            return; // Không xóa — có thể là lỗi tạm thời, giữ lại persist cũ
        }

        const { user, login, token, success, exp } = authState;

        // Không persist nếu token không hợp lệ
        const isInvalidToken = !token ||
            token === 'null' ||
            token === 'undefined' ||
            token === '';

        if (isInvalidToken) {
            // Giữ nguyên persist cũ — không ghi đè bằng state hỏng
            return;
        }

        // Strip các field nặng trong user (avatar base64, v.v.)
        const safeUser = user ? (({ avatar, cover, ...rest }) => rest)(user) : null;

        localStorage.setItem(
            AUTH_PERSIST_KEY,
            JSON.stringify({ user: safeUser, login, token, success, exp })
        );
    } catch (e) {
        if (e && e.name === 'QuotaExceededError') {
            console.warn('[authPersist] localStorage quota exceeded, skipping persist.');
        }
    }
};

/**
 * Xóa toàn bộ auth persist (chỉ gọi khi LOGOUT thực sự).
 */
export const clearPersistedAuth = () => {
    try {
        localStorage.removeItem(AUTH_PERSIST_KEY);
        localStorage.removeItem(TOKEN_KEY);
    } catch {}
};